import React, { useState } from "react";
import { Body2, FormLabel, SolidButton, defaultSkeletonMixin } from "@iventis/styles";
import { SsoConfig } from "@iventis/domain-model/model/ssoConfig";
import { useIventisTranslate } from "@iventis/translations/use-iventis-translate";
import { Content } from "@iventis/translations";
import styled from "@emotion/styled";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import { Paper } from "@mui/material";
import { SsoConfigApiUpdateRequest } from "@iventis/domain-model/model/ssoConfigApiUpdateRequest";
import { TextArea } from "./text-area";
import CustomSwitchComponent from "./switch-component";
import { DisabledOverlay } from "./disabled-overlay";
import { ConfirmationDialogComponent } from "./confirmation-dialog";

export const SsoConfigForm: React.FC<{
    ssoConfig: SsoConfig;
    apiBaseUrl: string;
    loading: boolean;
    onSubmit: (config: SsoConfigApiUpdateRequest) => Promise<void>;
}> = ({ ssoConfig, loading, onSubmit, apiBaseUrl }) => {
    const rows = 4;
    const translate = useIventisTranslate();

    const [confirmationDialog, setConfirmationDialog] = useState<"enable" | "disable" | null>(null);

    return (
        <>
            <StyledPaper elevation={2}>
                <Body2>{translate(Content.settings3.sso.setup_description)}:</Body2>

                <StyledPropertyRow>
                    <BoldBody2>{translate(Content.settings3.sso.entity_id)}:</BoldBody2> <Body2>{`${apiBaseUrl}/permissions/Saml`}</Body2>
                </StyledPropertyRow>
                <StyledPropertyRow>
                    <BoldBody2>{translate(Content.settings3.sso.rely_url)}:</BoldBody2> <Body2>{`${apiBaseUrl}/permissions/Saml2/Acs`}</Body2>
                </StyledPropertyRow>
                <StyledPropertyRow>
                    <BoldBody2>{translate(Content.settings3.sso.signon_url)}:</BoldBody2> <Body2>{`${apiBaseUrl}/permissions/authentication/saml-login`}</Body2>
                </StyledPropertyRow>
            </StyledPaper>
            {loading && <SsoFormSkeleton />}
            {!loading && (
                <Formik
                    initialValues={{
                        responseSigningCertificate: ssoConfig?.responseSigningCertificate ?? "",
                        metaDataLocation: ssoConfig?.metaDataLocation ?? "",
                        identityProvider: ssoConfig?.identityProvider ?? "",
                        enabled: ssoConfig?.enabled ?? false,
                    }}
                    validationSchema={Yup.object({
                        responseSigningCertificate: Yup.string().when("enabled", {
                            is: true,
                            then: (schema) => schema.required(),
                        }),
                        metaDataLocation: Yup.string().when("enabled", {
                            is: true,
                            then: (schema) => schema.required(),
                        }),
                        identityProvider: Yup.string().when("enabled", {
                            is: true,
                            then: (schema) => schema.required(),
                        }),
                        enabled: Yup.boolean().required(),
                    })}
                    onSubmit={async (values, helpers) => {
                        await onSubmit({
                            responseSigningCertificate: values.responseSigningCertificate,
                            metaDataLocation: values.metaDataLocation,
                            identityProvider: values.identityProvider,
                            enabled: values.enabled,
                        });
                        helpers.setTouched({});
                    }}
                >
                    {(props) => {
                        const { touched, values, isSubmitting, setFieldValue, setFieldTouched, isValid, handleSubmit } = props;
                        return (
                            <StyledFormContainer onSubmit={props.handleSubmit} placeholder={undefined} onPointerEnterCapture={undefined} onPointerLeaveCapture={undefined}>
                                <StyledInputsContainer>
                                    {!values.enabled && <DisabledOverlay />}
                                    <StyledInputSection>
                                        <FormLabel>{translate(Content.settings3.sso.response_signing_certificate)}</FormLabel>
                                        <TextArea
                                            value={values.responseSigningCertificate}
                                            onChange={(value) => {
                                                setFieldTouched("responseSigningCertificate", true);
                                                setFieldValue("responseSigningCertificate", value);
                                            }}
                                            rows={rows}
                                            error={null}
                                            placeholder={null}
                                            dataTestId="sso-response-signing-certificate-id"
                                            disabled={!values.enabled}
                                        />
                                    </StyledInputSection>
                                    <StyledInputSection>
                                        <FormLabel>{translate(Content.settings3.sso.meta_data_location)}</FormLabel>
                                        <TextArea
                                            value={values.metaDataLocation}
                                            onChange={(value) => {
                                                setFieldTouched("metaDataLocation", true);
                                                setFieldValue("metaDataLocation", value);
                                            }}
                                            rows={rows}
                                            error={null}
                                            placeholder={null}
                                            dataTestId="sso-meta-data-location"
                                            disabled={!values.enabled}
                                        />
                                    </StyledInputSection>
                                    <StyledInputSection>
                                        <FormLabel>{translate(Content.settings3.sso.identity_provider)}</FormLabel>
                                        <TextArea
                                            value={values.identityProvider}
                                            onChange={(value) => {
                                                setFieldTouched("identityProvider", true);
                                                setFieldValue("identityProvider", value);
                                            }}
                                            rows={rows}
                                            error={null}
                                            placeholder={null}
                                            dataTestId="sso-identity-provider"
                                            disabled={!values.enabled}
                                        />
                                    </StyledInputSection>
                                </StyledInputsContainer>
                                <SubmitButtonContainer>
                                    <CustomSwitchComponent
                                        checked={values.enabled}
                                        label={translate(Content.settings3.sso.sso_enabled)}
                                        onChange={(e) => {
                                            setFieldTouched("enabled", true);
                                            setFieldValue("enabled", e.target.checked);
                                        }}
                                        testId="toggle-sso-enabled"
                                    />
                                    <StyledSubmitButton
                                        onClick={() => {
                                            const switchingOff = touched.enabled && !values.enabled;
                                            const switchingOn = touched.enabled && values.enabled;
                                            if (switchingOff || switchingOn) {
                                                setConfirmationDialog(switchingOff ? "disable" : "enable");
                                            } else {
                                                handleSubmit();
                                            }
                                        }}
                                        disabled={isSubmitting || !isValid || Object.keys(touched).length === 0}
                                    >
                                        {translate(Content.common.buttons.save)}
                                    </StyledSubmitButton>
                                </SubmitButtonContainer>

                                {/* Disable confirmation */}
                                <ConfirmationDialogComponent
                                    handleConfirm={() => {
                                        handleSubmit();
                                        setConfirmationDialog(null);
                                    }}
                                    title={translate(Content.settings3.sso.disable_confirmation)}
                                    confirmText={translate(Content.common.buttons.disable)}
                                    cancelText={translate(Content.common.buttons.cancel)}
                                    show={confirmationDialog === "disable"}
                                    handleCancel={() => setConfirmationDialog(null)}
                                    dataTestId="disable-sso-confirmation"
                                >
                                    <Body2>{translate(Content.settings3.sso.disable_confirmation_warning)}</Body2>
                                    <Body2>{translate(Content.settings3.sso.disable_confirmation_warning2)}</Body2>
                                </ConfirmationDialogComponent>

                                {/* Enable confirmation */}
                                <ConfirmationDialogComponent
                                    handleConfirm={() => {
                                        handleSubmit();
                                        setConfirmationDialog(null);
                                    }}
                                    title={translate(Content.settings3.sso.enable_confirmation)}
                                    confirmText={translate(Content.common.buttons.enable)}
                                    cancelText={translate(Content.common.buttons.cancel)}
                                    show={confirmationDialog === "enable"}
                                    handleCancel={() => setConfirmationDialog(null)}
                                    dataTestId="enable-sso-confirmation"
                                >
                                    <Body2>{translate(Content.settings3.sso.enable_confirmation_warning)}</Body2>
                                </ConfirmationDialogComponent>
                            </StyledFormContainer>
                        );
                    }}
                </Formik>
            )}
        </>
    );
};
const StyledSubmitButton = styled(SolidButton)`
    min-width: 150px;
`;

const StyledInputsContainer = styled.div`
    position: relative;
`;

const StyledInputSection = styled.div`
    display: flex;
    flex-direction: column;
`;

const SubmitButtonContainer = styled.div`
    display: flex;
    justify-content: flex-end;
    gap: 10px;
`;

const StyledFormContainer = styled(Form)`
    display: flex;
    flex-direction: column;
    width: 100%;
    gap: 10px;
`;

const SkeletonPlaceholder = styled.span`
    ${defaultSkeletonMixin}
`;

const LabelSkeleton = styled.div`
    height: 1em;
    width: 100px;
`;

const TextAreaSkeleton = styled.div`
    height: 7em;
    width: 100%;
`;

const StyledPropertyRow = styled.div`
    display: flex;
    gap: 5px;
`;

const StyledPaper = styled(Paper)`
    padding: 10px;
    margin-bottom: 10px;
    display: flex;
    flex-direction: column;
    gap: 10px;
`;

const BoldBody2 = styled(Body2)`
    font-weight: bold;
`;

const SsoFormSkeleton = () => (
    <>
        <LabelSkeleton>
            <SkeletonPlaceholder />
        </LabelSkeleton>
        <TextAreaSkeleton>
            <SkeletonPlaceholder />
        </TextAreaSkeleton>

        <LabelSkeleton>
            <SkeletonPlaceholder />
        </LabelSkeleton>
        <TextAreaSkeleton>
            <SkeletonPlaceholder />
        </TextAreaSkeleton>

        <LabelSkeleton>
            <SkeletonPlaceholder />
        </LabelSkeleton>
        <TextAreaSkeleton>
            <SkeletonPlaceholder />
        </TextAreaSkeleton>
    </>
);
